import { usePageTitle } from '../hooks/usePageTitle';
import React, { useState, useEffect, useCallback } from 'react';
import { standardAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus, Save, Trash2, ChevronDown, ChevronRight, GripVertical,
  ChevronUp, Edit2, ListChecks, ClipboardList, BookOpen, Layers,
  FileText, X, Copy,
} from 'lucide-react';

const PhaseMasterPage = () => {
  usePageTitle('Phase Master');
  const { isAdmin } = useAuth();
  const [standards, setStandards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStdId, setSelectedStdId] = useState(null);
  const [expandedPhaseIdx, setExpandedPhaseIdx] = useState(null);
  const [editingPhases, setEditingPhases] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // New standard dialog
  const [showNewStd, setShowNewStd] = useState(false);
  const [newStdForm, setNewStdForm] = useState({ code: '', label: '', group: '', category: 'new' });

  const fetchStandards = useCallback(async () => {
    try {
      const res = await standardAPI.getAll();
      setStandards(res.data);
      if (!selectedStdId && res.data.length > 0) {
        selectStandard(res.data[0]);
      }
    } catch { toast.error('Failed to load standards'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStandards(); }, [fetchStandards]);

  const selectStandard = (std) => {
    if (hasChanges && !window.confirm('You have unsaved changes. Discard?')) return;
    setSelectedStdId(std.id);
    setEditingPhases(JSON.parse(JSON.stringify(std.phases || [])));
    setExpandedPhaseIdx(null);
    setHasChanges(false);
  };

  const selectedStd = standards.find(s => s.id === selectedStdId);

  // Phase operations
  const addPhase = () => {
    const order = (editingPhases?.length || 0) + 1;
    setEditingPhases(prev => [...(prev || []), {
      id: '', code: `custom_phase_${order}`, label: `New Phase ${order}`, order,
      checklist_items: [], task_templates: [],
    }]);
    setExpandedPhaseIdx((editingPhases?.length || 0));
    setHasChanges(true);
  };

  const removePhase = (idx) => {
    if (!window.confirm(`Delete phase "${editingPhases[idx]?.label}"? This won't affect existing clients.`)) return;
    setEditingPhases(prev => prev.filter((_, i) => i !== idx).map((p, i) => ({ ...p, order: i + 1 })));
    setExpandedPhaseIdx(null);
    setHasChanges(true);
  };

  const updatePhaseField = (idx, field, value) => {
    setEditingPhases(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
    setHasChanges(true);
  };

  const movePhase = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= editingPhases.length) return;
    const arr = [...editingPhases];
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    setEditingPhases(arr.map((p, i) => ({ ...p, order: i + 1 })));
    setExpandedPhaseIdx(target);
    setHasChanges(true);
  };

  // Checklist operations
  const addChecklist = (phaseIdx) => {
    setEditingPhases(prev => prev.map((p, i) => i === phaseIdx ? {
      ...p, checklist_items: [...(p.checklist_items || []), { title: '', description: '' }]
    } : p));
    setHasChanges(true);
  };

  const updateChecklist = (phaseIdx, checkIdx, field, value) => {
    setEditingPhases(prev => prev.map((p, i) => i === phaseIdx ? {
      ...p, checklist_items: p.checklist_items.map((c, ci) => ci === checkIdx ? { ...c, [field]: value } : c)
    } : p));
    setHasChanges(true);
  };

  const removeChecklist = (phaseIdx, checkIdx) => {
    setEditingPhases(prev => prev.map((p, i) => i === phaseIdx ? {
      ...p, checklist_items: p.checklist_items.filter((_, ci) => ci !== checkIdx)
    } : p));
    setHasChanges(true);
  };

  // Task template operations
  const addTask = (phaseIdx) => {
    setEditingPhases(prev => prev.map((p, i) => i === phaseIdx ? {
      ...p, task_templates: [...(p.task_templates || []), { title: '', description: '', priority: 'medium' }]
    } : p));
    setHasChanges(true);
  };

  const updateTask = (phaseIdx, taskIdx, field, value) => {
    setEditingPhases(prev => prev.map((p, i) => i === phaseIdx ? {
      ...p, task_templates: p.task_templates.map((t, ti) => ti === taskIdx ? { ...t, [field]: value } : t)
    } : p));
    setHasChanges(true);
  };

  const removeTask = (phaseIdx, taskIdx) => {
    setEditingPhases(prev => prev.map((p, i) => i === phaseIdx ? {
      ...p, task_templates: p.task_templates.filter((_, ti) => ti !== taskIdx)
    } : p));
    setHasChanges(true);
  };

  // Save
  const handleSave = async () => {
    if (!selectedStdId || !editingPhases) return;
    setSaving(true);
    try {
      await standardAPI.update(selectedStdId, { phases: editingPhases });
      toast.success('Phases saved! Changes will apply to new clients.');
      setHasChanges(false);
      fetchStandards();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  // New standard
  const handleCreateStandard = async () => {
    if (!newStdForm.code || !newStdForm.label) { toast.error('Code and Label are required'); return; }
    try {
      const defaultPhases = [
        { code: 'gap_analysis', label: 'Phase 1: Gap Analysis', order: 1, checklist_items: [], task_templates: [] },
        { code: 'documentation', label: 'Phase 2: Documentation', order: 2, checklist_items: [], task_templates: [] },
        { code: 'implementation', label: 'Phase 3: Implementation', order: 3, checklist_items: [], task_templates: [] },
        { code: 'assessment', label: 'Phase 4: Assessment', order: 4, checklist_items: [], task_templates: [] },
        { code: 'post_assessment', label: 'Phase 5: Post-Assessment', order: 5, checklist_items: [], task_templates: [] },
      ];
      const res = await standardAPI.create({ ...newStdForm, phases: defaultPhases });
      toast.success('Standard created');
      setShowNewStd(false);
      setNewStdForm({ code: '', label: '', group: '', category: 'new' });
      await fetchStandards();
      selectStandard(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-5 animate-in" data-testid="phase-master-page">
      {/* Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-[#0f4c81] via-[#1a6cb4] to-[#2196F3] p-6 text-white">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.3) 0%, transparent 50%)'}} />
        <div className="relative z-10 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><Layers className="w-7 h-7" /> Phase Master</h1>
            <p className="text-blue-100 text-sm mt-1">Edit phase templates, checklists and task templates per accreditation standard</p>
          </div>
          <div className="flex gap-2">
            {hasChanges && (
              <Button onClick={handleSave} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="save-phases-btn">
                {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
            )}
            {isAdmin() && (
              <Button onClick={() => setShowNewStd(true)} className="bg-white/20 hover:bg-white/30 text-white border border-white/30" data-testid="new-standard-btn">
                <Plus className="w-4 h-4 mr-2" /> New Standard
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-5">
        {/* Left: Standards List */}
        <div className="col-span-12 lg:col-span-3 space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1 mb-2">Accreditation Standards</p>
          {standards.map(std => (
            <button
              key={std.id}
              onClick={() => selectStandard(std)}
              className={`w-full text-left p-3 rounded-lg border transition-all ${
                selectedStdId === std.id
                  ? 'bg-blue-50 border-blue-300 shadow-sm'
                  : 'bg-white border-slate-200 hover:border-blue-200 hover:bg-blue-50/50'
              } ${!std.is_active ? 'opacity-50' : ''}`}
              data-testid={`std-select-${std.id}`}
            >
              <div className="flex items-center gap-2">
                <BookOpen className={`w-4 h-4 shrink-0 ${selectedStdId === std.id ? 'text-blue-600' : 'text-slate-400'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{std.label}</p>
                  <p className="text-xs text-muted-foreground">{std.phases?.length || 0} phases</p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Right: Phase Editor */}
        <div className="col-span-12 lg:col-span-9">
          {!selectedStd ? (
            <Card className="p-12 text-center">
              <Layers className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-muted-foreground">Select a standard to edit its phases</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {/* Standard info */}
              <Card className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <h2 className="font-bold text-lg text-slate-800">{selectedStd.label}</h2>
                    <p className="text-sm text-muted-foreground">
                      Code: <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded text-xs">{selectedStd.code}</span>
                      {selectedStd.group && <span className="ml-3">Group: {selectedStd.group}</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-blue-100 text-blue-700">{editingPhases?.length || 0} Phases</Badge>
                    <Badge className="bg-amber-100 text-amber-700">
                      {editingPhases?.reduce((sum, p) => sum + (p.checklist_items?.length || 0), 0) || 0} Checklists
                    </Badge>
                    <Badge className="bg-emerald-100 text-emerald-700">
                      {editingPhases?.reduce((sum, p) => sum + (p.task_templates?.length || 0), 0) || 0} Tasks
                    </Badge>
                  </div>
                </div>
              </Card>

              {/* Phases */}
              {editingPhases?.map((phase, idx) => (
                <Card key={idx} className={`overflow-hidden transition-all ${expandedPhaseIdx === idx ? 'ring-2 ring-blue-300' : ''}`} data-testid={`phase-card-${idx}`}>
                  {/* Phase header */}
                  <div
                    className="flex items-center gap-2 p-3 cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => setExpandedPhaseIdx(expandedPhaseIdx === idx ? null : idx)}
                  >
                    <div className="flex flex-col gap-0.5">
                      <button onClick={e => { e.stopPropagation(); movePhase(idx, -1); }} className="text-slate-300 hover:text-slate-600" disabled={idx === 0}><ChevronUp className="w-3.5 h-3.5" /></button>
                      <GripVertical className="w-3.5 h-3.5 text-slate-200" />
                      <button onClick={e => { e.stopPropagation(); movePhase(idx, 1); }} className="text-slate-300 hover:text-slate-600" disabled={idx === editingPhases.length - 1}><ChevronDown className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm shrink-0">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{phase.label}</p>
                      <p className="text-xs text-muted-foreground">
                        <span className="font-mono">{phase.code}</span>
                        <span className="mx-2">|</span>
                        {phase.checklist_items?.length || 0} checklists, {phase.task_templates?.length || 0} tasks
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={e => { e.stopPropagation(); removePhase(idx); }} data-testid={`remove-phase-${idx}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                      </Button>
                      {expandedPhaseIdx === idx ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                    </div>
                  </div>

                  {/* Expanded editor */}
                  {expandedPhaseIdx === idx && (
                    <div className="border-t p-4 bg-slate-50/80 space-y-5">
                      {/* Phase details */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Phase Code</Label>
                          <Input value={phase.code} onChange={e => updatePhaseField(idx, 'code', e.target.value.toLowerCase().replace(/\s+/g, '_'))} className="h-9 text-sm" data-testid={`phase-code-${idx}`} />
                        </div>
                        <div>
                          <Label className="text-xs">Phase Label</Label>
                          <Input value={phase.label} onChange={e => updatePhaseField(idx, 'label', e.target.value)} className="h-9 text-sm" data-testid={`phase-label-${idx}`} />
                        </div>
                      </div>

                      {/* Checklist Items */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-semibold flex items-center gap-1.5 text-slate-700">
                            <ListChecks className="w-4 h-4 text-amber-600" /> Checklist Items ({phase.checklist_items?.length || 0})
                          </h4>
                          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => addChecklist(idx)} data-testid={`add-checklist-${idx}`}>
                            <Plus className="w-3 h-3 mr-1" /> Add Checklist
                          </Button>
                        </div>
                        {(phase.checklist_items || []).length === 0 ? (
                          <p className="text-xs text-muted-foreground italic py-2 text-center">No checklist items. Click "Add Checklist" to create one.</p>
                        ) : (
                          <div className="space-y-1.5 max-h-60 overflow-y-auto">
                            {(phase.checklist_items || []).map((cl, ci) => (
                              <div key={ci} className="flex items-start gap-2 p-2 bg-white rounded border border-slate-200 group">
                                <span className="text-xs text-slate-400 font-mono mt-2 shrink-0 w-5">{ci + 1}.</span>
                                <div className="flex-1 space-y-1">
                                  <Input
                                    value={cl.title || ''}
                                    onChange={e => updateChecklist(idx, ci, 'title', e.target.value)}
                                    placeholder="Checklist item title..."
                                    className="h-7 text-xs"
                                    data-testid={`checklist-title-${idx}-${ci}`}
                                  />
                                  <Input
                                    value={cl.description || ''}
                                    onChange={e => updateChecklist(idx, ci, 'description', e.target.value)}
                                    placeholder="Description (optional)"
                                    className="h-7 text-xs text-muted-foreground"
                                  />
                                </div>
                                <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 shrink-0" onClick={() => removeChecklist(idx, ci)}>
                                  <X className="w-3 h-3 text-red-400" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Task Templates */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-semibold flex items-center gap-1.5 text-slate-700">
                            <ClipboardList className="w-4 h-4 text-emerald-600" /> Task Templates ({phase.task_templates?.length || 0})
                          </h4>
                          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => addTask(idx)} data-testid={`add-task-${idx}`}>
                            <Plus className="w-3 h-3 mr-1" /> Add Task
                          </Button>
                        </div>
                        {(phase.task_templates || []).length === 0 ? (
                          <p className="text-xs text-muted-foreground italic py-2 text-center">No task templates. Click "Add Task" to create one.</p>
                        ) : (
                          <div className="space-y-1.5 max-h-60 overflow-y-auto">
                            {(phase.task_templates || []).map((task, ti) => (
                              <div key={ti} className="flex items-start gap-2 p-2 bg-white rounded border border-slate-200 group">
                                <span className="text-xs text-slate-400 font-mono mt-2 shrink-0 w-5">{ti + 1}.</span>
                                <div className="flex-1 space-y-1">
                                  <Input
                                    value={task.title || ''}
                                    onChange={e => updateTask(idx, ti, 'title', e.target.value)}
                                    placeholder="Task title..."
                                    className="h-7 text-xs"
                                    data-testid={`task-title-${idx}-${ti}`}
                                  />
                                  <Input
                                    value={task.description || ''}
                                    onChange={e => updateTask(idx, ti, 'description', e.target.value)}
                                    placeholder="Description (optional)"
                                    className="h-7 text-xs text-muted-foreground"
                                  />
                                </div>
                                <Select value={task.priority || 'medium'} onValueChange={v => updateTask(idx, ti, 'priority', v)}>
                                  <SelectTrigger className="h-7 w-24 text-xs shrink-0"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="low">Low</SelectItem>
                                    <SelectItem value="medium">Medium</SelectItem>
                                    <SelectItem value="high">High</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 shrink-0" onClick={() => removeTask(idx, ti)}>
                                  <X className="w-3 h-3 text-red-400" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </Card>
              ))}

              {/* Add Phase Button */}
              {isAdmin() && (
                <button
                  onClick={addPhase}
                  className="w-full p-3 border-2 border-dashed border-slate-300 rounded-lg text-slate-400 hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50/50 transition-all flex items-center justify-center gap-2 text-sm"
                  data-testid="add-phase-btn"
                >
                  <Plus className="w-4 h-4" /> Add New Phase
                </button>
              )}

              {/* Floating save bar */}
              {hasChanges && (
                <div className="sticky bottom-4 bg-blue-600 text-white rounded-xl p-3 flex items-center justify-between shadow-lg">
                  <p className="text-sm">You have unsaved changes to <strong>{selectedStd.label}</strong></p>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" className="text-white/80 hover:text-white hover:bg-white/20" onClick={() => { setEditingPhases(JSON.parse(JSON.stringify(selectedStd.phases || []))); setHasChanges(false); }}>
                      Discard
                    </Button>
                    <Button size="sm" className="bg-white text-blue-700 hover:bg-blue-50" onClick={handleSave} disabled={saving} data-testid="save-floating-btn">
                      {saving ? <div className="w-4 h-4 border-2 border-blue-300 border-t-blue-700 rounded-full animate-spin mr-2" /> : <Save className="w-4 h-4 mr-1" />}
                      Save
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* New Standard Dialog */}
      <Dialog open={showNewStd} onOpenChange={setShowNewStd}>
        <DialogContent className="max-w-md" data-testid="new-standard-dialog">
          <DialogHeader>
            <DialogTitle>Create New Accreditation Standard</DialogTitle>
            <DialogDescription>5 default phases will be created. You can customize them after.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Code *</Label>
              <Input value={newStdForm.code} onChange={e => setNewStdForm(f => ({ ...f, code: e.target.value.toLowerCase().replace(/\s+/g, '_') }))} placeholder="e.g. nabh_hospital_new" data-testid="new-std-code" />
            </div>
            <div>
              <Label>Label *</Label>
              <Input value={newStdForm.label} onChange={e => setNewStdForm(f => ({ ...f, label: e.target.value }))} placeholder="e.g. NABH Hospital - New" data-testid="new-std-label" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Group</Label>
                <Input value={newStdForm.group} onChange={e => setNewStdForm(f => ({ ...f, group: e.target.value }))} placeholder="e.g. NABH" />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={newStdForm.category} onValueChange={v => setNewStdForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="renewal">Renewal</SelectItem>
                    <SelectItem value="surveillance">Surveillance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewStd(false)}>Cancel</Button>
            <Button onClick={handleCreateStandard} data-testid="create-std-btn">Create Standard</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PhaseMasterPage;
